import { DocumentObject, deserialize, serialize } from '@jsonapiworld/jsonapi-serializer';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { backendApiGateway } from '~/client/backend';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics';
import { Company, CompanyQueryKeys } from '~/types/company';
import { CompanyIdentity, CompanyIdentityQueryKeys } from '~/types/company-identity';
import { InvoicesQueryKeys } from '~/types/invoicing';
import { buildJsonAPIQueryParams, jsonAPIIncludeBuilder } from '~/utils/jsonapi-query';

const searchCompanyMetaSchema = z.object({ available: z.boolean() });

const searchCompanyIdentity = async (values: { country: string; taxNumber: string }) => {
  const { data } = await backendApiGateway.post<DocumentObject & { meta: { available: boolean } }>(
    '/api/v1/company-identities/search',
    undefined,
    {
      params: { tax_number: values.taxNumber.trim(), country: values.country.trim() },
    },
  );
  const meta = searchCompanyMetaSchema.parse(data.meta);

  return {
    data: deserialize<CompanyIdentity>(data) as CompanyIdentity,
    meta,
  };
};

export const useSearchCompanyIdentity = () => {
  return useMutation({
    mutationFn: searchCompanyIdentity,
  });
};

interface CompanyRequest {
  companyIdentity?: CompanyIdentity;
}

const fetchCompanyIdentity = async (companyId: Company['id']) => {
  const includeBuilder = jsonAPIIncludeBuilder(['companyIdentity']);
  const queryParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/companies/${companyId}`, {
    params: queryParams,
  });

  const identities = deserialize<CompanyRequest>(data) as CompanyRequest;
  return identities.companyIdentity;
};

export const useActiveCompanyIdentity = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: CompanyIdentityQueryKeys.company(companyId),
    queryFn: () => fetchCompanyIdentity(companyId),
    enabled: Boolean(companyId),
  });
};

const updateCompanyIdentity = async (id: CompanyIdentity['id'], values: Partial<CompanyIdentity>) => {
  const payload = serialize(
    {
      id,
      ...values,
    },
    'company-identity',
  );

  await backendApiGateway.patch<DocumentObject>(`/api/v1/company-identities/${id}`, payload);
};

export const useUpdateCompanyIdentity = () => {
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: (values: Partial<CompanyIdentity> & { id: CompanyIdentity['id'] }) =>
      updateCompanyIdentity(values.id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CompanyIdentityQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
      track(AnalyticsEvents.COMPANY_IDENTITY_DATA_UPDATED, { context: 'document-builder' });
    },
  });
};
